import { useEffect, useRef, useState } from "react";
import { getDigiPin } from "../../../utils/digipin";
import "../../../style/map.css";
import { LocationData } from "../types/LocationData";

type Provider = "google" | "mappls";

interface VectorMapProps {
    provider: "google" | "mappls";
    onConfirm: (data: LocationData) => void;
}

const DEFAULT_LAT = 28.6139;
const DEFAULT_LNG = 77.2090;

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY;
const MAPPLS_TOKEN = process.env.REACT_APP_MAPPLS_TOKEN;

declare global {
    interface Window {
        google: any;
        mappls: any;
    }
}

const loadScript = (src: string) =>
    new Promise<void>((resolve) => {
        if (document.querySelector(`script[src="${src}"]`)) {
            resolve();
            return;
        }

        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        document.body.appendChild(s);
    });


const VectorMap: React.FC<VectorMapProps> = ({ provider, onConfirm }) => {
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<any>(null);
    const markerRef = useRef<any>(null);
    const autoRef = useRef<HTMLInputElement>(null);

    const [latLng, setLatLng] = useState<any>(null);
    const [digiPin, setDigiPin] = useState("");
    const [address, setAddress] = useState("");
   // const [accuracy, setAccuracy] = useState<number | null>(null);
    const [accuracy, setAccuracy] = useState<number | undefined>(undefined);


    useEffect(() => {
        (async () => {
            markerRef.current = null;
            mapRef.current = null;

            if (provider === "google") {
                await loadScript(
                    `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_KEY}&libraries=places`
                );
            } else {
                await loadScript(
                    `https://sdk.mappls.com/map/sdk/web?v=3.0&access_token=${MAPPLS_TOKEN}`
                );
                await loadScript(
                    `https://sdk.mappls.com/map/sdk/plugins?v=3.0&access_token=${MAPPLS_TOKEN}`
                );
            }

            initMap();
        })();
    }, [provider]);


    const initMap = () => {
        if (provider === "google" && window.google) {
            mapRef.current = new window.google.maps.Map(
                mapContainerRef.current!,
                {
                    center: { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
                    zoom: 15,
                }
            );

            createOrMoveMarker(DEFAULT_LAT, DEFAULT_LNG);

            const autocomplete = new window.google.maps.places.Autocomplete(
                autoRef.current!,
                {
                    fields: ["geometry", "formatted_address"],
                    componentRestrictions: { country: "in" }, // 🇮🇳 India only
                }
            );

            // Disable until 3 chars
            autoRef.current!.addEventListener("input", () => {
                if (autoRef.current!.value.length < 3) {
                    autocomplete.set("place", null);
                    return;
                }
            });

            autocomplete.addListener("place_changed", () => {
                const inputValue = autoRef.current!.value;
                if (inputValue.length < 3) return;

                const place = autocomplete.getPlace();
                if (!place.geometry) return;

                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();

                updateLocation(lat, lng);
                setAddress(place.formatted_address || "");
            });
        }


        if (provider === "mappls" && window.mappls) {
            mapRef.current = new window.mappls.Map("map", {
                center: [DEFAULT_LNG, DEFAULT_LAT],
                zoom: 15,
            });

            createOrMoveMarker(DEFAULT_LAT, DEFAULT_LNG);

            let debounceTimer: any;

            autoRef.current!.addEventListener("input", () => {
                const value = autoRef.current!.value;

                if (value.length < 3) return;

                clearTimeout(debounceTimer);

                debounceTimer = setTimeout(() => {
                    new window.mappls.search(
                        autoRef.current,
                        {
                            region: "IND", // 🇮🇳 India only
                            location: [DEFAULT_LAT, DEFAULT_LNG],
                        },
                        (data: any) => {
                            if (!data || !data[0]) return;
                            const lat = data[0].latitude;
                            const lng = data[0].longitude;

                            updateLocation(lat, lng);
                        }
                    );
                }, 400); // debounce for production
            });
        }

    };

    const updateLocation = (lat: number, lng: number) => {
        setLatLng({ lat, lng });
        setDigiPin(getDigiPin(lat, lng));
        createOrMoveMarker(lat, lng);
        reverseGeocode(lat, lng);
    };

    const createOrMoveMarker = (lat: number, lng: number) => {
        if (provider === "google") {
            if (!markerRef.current) {
                markerRef.current = new window.google.maps.Marker({
                    map: mapRef.current,
                    position: { lat, lng },
                    draggable: true,
                });

                markerRef.current.addListener("dragend", (e: any) => {
                    updateLocation(e.latLng.lat(), e.latLng.lng());
                });
            } else {
                markerRef.current.setPosition({ lat, lng });
            }
            mapRef.current.setCenter({ lat, lng });
        }

        if (provider === "mappls") {
            if (!markerRef.current) {
                markerRef.current = new window.mappls.Marker({
                    map: mapRef.current,
                    position: { lat, lng },
                    draggable: true,
                });

                markerRef.current.addListener("dragend", () => {
                    const pos = markerRef.current.getPosition();
                    updateLocation(pos.lat, pos.lng);
                });
            } else {
                markerRef.current.setPosition({ lat, lng });
            }

            mapRef.current.setCenter([lng, lat]);
        }
    };

    const reverseGeocode = async (lat: number, lng: number) => {
        if (provider === "google") {
            // const res = await fetch(
            //     `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
            // );
            // const data = await res.json();
            // setAddress(data.display_name || "");
            const res = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_KEY}`
            );
            const data = await res.json();
            setAddress(data.results?.[0]?.formatted_address || "");
        }

        if (provider === "mappls") {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
            );
            const data = await res.json();
            setAddress(data.display_name || "");
        }
    };

    const detectLocation = () => {


    // ✅ clear manually typed address
    setAddress("");

    if (autoRef.current) {
        autoRef.current.value = "";
    }

        if (!navigator.geolocation) return;

        const watchId = navigator.geolocation.watchPosition(
            (pos) => {
                const { latitude, longitude, accuracy } = pos.coords;
                setAccuracy(accuracy);
                updateLocation(latitude, longitude);

                if (accuracy && accuracy < 500) {
                    navigator.geolocation.clearWatch(watchId);
                }
            },
            console.error,
            { enableHighAccuracy: true, timeout: 60000, maximumAge: 0 }
        );
    };

    return (
        <div className="map-container">
            <div className="map-search-box">
                {/* <input
                    ref={autoRef}
                    type="text"
                    className="form-control mb-3"
                    placeholder="Search place / address"
                /> */}

                <input
    ref={autoRef}
    type="text"
    className="form-control mb-3"
    placeholder="Search place / address"
    value={address}
    onChange={(e) => setAddress(e.target.value)}
/>
            </div>

            <div ref={mapContainerRef} className="map-view" />

            <div className="map-actions">
                <div className="digipin-box">
                    <strong>DIGIPIN:</strong>
                    <span>{digiPin || "—"}</span>
                </div>

                {accuracy && (
                    <div className="accuracy-box">
                        Accuracy: ±{Math.round(accuracy)} meters
                    </div>
                )}

                <button
                    className="btn btn-outline-primary w-100"
                    onClick={detectLocation}
                    type="button"
                >
                    📍 Use My Current Location
                </button>

                {latLng && (
                    
                        <button
                            type="button"
                            className="btn btn-success confirm-location w-100"
                            onClick={() =>
                                onConfirm({
                                    latitude: latLng.lat,
                                    longitude: latLng.lng,
                                    digiPin,
                                    address,
                                    accuracy,
                                })
                            }
                        >
                            Confirm Location
                        </button>
                    
                )}
            </div>
        </div>
    );
};

export default VectorMap;
